import {
  ChangeEvent,
  KeyboardEvent,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  ArrowUpwardIcon as ChatBVArrowUpwardIcon,
  SendButton as ChatBVSendButton,
  StyledInputChat as ChatBVStyledInputChat,
} from 'components/ChatBV/components';

import { DeepSearchContext } from '../context';

import { A2uiReportRenderer } from './A2uiReportRenderer';
import {
  PageContainer,
  MessagesArea,
  MessageBubble,
  InputContainer,
  InputWrapper,
  LandingContainer,
  LandingTitle,
  LandingSubtitle,
  TypingIndicator,
} from './DeepSearchPage.styles';

export const DeepSearchPage = () => {
  const {
    activeMessages: messages,
    isLoading,
    sendMessage,
  } = useContext(DeepSearchContext);
  const [inputValue, setInputValue] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    document.body.classList.add('deepsearch-page');
    return () => {
      document.body.classList.remove('deepsearch-page');
    };
  }, []);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages, isLoading]);

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) {
      return;
    }
    sendMessage(inputValue);
    setInputValue('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    setInputValue(e.target.value);
    const textarea = e.target;
    textarea.style.height = 'auto';
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const showLanding = messages.length === 0;

  return (
    <PageContainer>
      {showLanding ? (
        <LandingContainer>
          <LandingTitle>Deep Search Agent</LandingTitle>
          <LandingSubtitle>
            Ask anything to start a deep search. Get comprehensive results
            powered by AI.
          </LandingSubtitle>
        </LandingContainer>
      ) : (
        <MessagesArea>
          {messages.map((message) => {
            if (Array.isArray(message.data)) {
              return (
                <A2uiReportRenderer key={message.id} messages={message.data} />
              );
            }
            return (
              <MessageBubble key={message.id} isUser={message.role === 'user'}>
                {message.data}
              </MessageBubble>
            );
          })}
          {isLoading && (
            <TypingIndicator>
              <span />
              <span />
              <span />
            </TypingIndicator>
          )}
          <div ref={messagesEndRef} />
        </MessagesArea>
      )}

      <InputContainer>
        <InputWrapper>
          <ChatBVStyledInputChat
            ref={textareaRef}
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            placeholder="Ask anything..."
            rows={1}
            aria-label="Search input"
            disabled={isLoading}
          />
          <ChatBVSendButton
            onClick={handleSend}
            hasInputValue={inputValue.trim().length > 0}
            disabled={!inputValue.trim() || isLoading}
            aria-label="Send message"
          >
            <ChatBVArrowUpwardIcon
              hasInputValue={inputValue.trim().length > 0}
            />
          </ChatBVSendButton>
        </InputWrapper>
      </InputContainer>
    </PageContainer>
  );
};
